"use client";

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSession, signOut } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useTheme } from "next-themes";
import {
  Search, X, FileText, ChevronDown, Check, ExternalLink, Link2,
  Plus, Library, Settings, Sun, Moon, Monitor, LogOut, Users, Zap,
} from "lucide-react";
import * as Lucide from "lucide-react";
import { API_URL, authOnlyHeaders, revokeServerSession } from "../../utils/api";
import { getTwemojiSvgUrl } from "../../utils/emoji";
import { useSidebarContext } from "../../contexts/SidebarContext";

// ── 타입 ─────────────────────────────────────────────────────────────────────

interface Doc {
  id: string;
  title: string;
  icon?: string | null;
  parentId?: string | null;
  breadcrumb?: string;
  workspaceType?: string;
  creatorName?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
  textSnippet?: string | null;
  // 검색어가 등장한 자리의 앞뒤 문맥. 왜 이 문서가 걸렸는지 보여 준다.
  matchContext?: string | null;
  matchedIn?: "title" | "body" | "comment" | "suggestion";
  isSuggestion?: boolean;
}

interface Command {
  id: string;
  icon: React.ReactNode;
  label: string;
  description: string;
  keywords: string[];
  action: () => void;
}

type FlatItem =
  | { type: "command"; command: Command }
  | { type: "doc"; doc: Doc };

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenSettings?: () => void;
}

// ── 날짜 그룹 ─────────────────────────────────────────────────────────────────

type Group = "today" | "week" | "month" | "older";
const GROUP_LABELS: Record<Group, string> = { today: "오늘", week: "지난주", month: "지난 30일", older: "이전" };

function toGroup(dateStr: string | null | undefined): Group {
  if (!dateStr) return "older";
  const diffDays = (Date.now() - new Date(dateStr).getTime()) / 86400000;
  if (diffDays < 1)  return "today";
  if (diffDays < 7)  return "week";
  if (diffDays < 30) return "month";
  return "older";
}

function groupDocs(docs: Doc[]) {
  const g: Record<Group, Doc[]> = { today: [], week: [], month: [], older: [] };
  for (const d of docs) g[toGroup(d.updatedAt)].push(d);
  return g;
}

// ── 아이콘 렌더 ───────────────────────────────────────────────────────────────

function DocIcon({ icon, size = 14 }: { icon?: string | null; size?: number }) {
  if (!icon) return <FileText size={size} className="text-gray-400 dark:text-zinc-500 shrink-0" />;
  if (icon.startsWith("emoji:")) {
    const code = icon.replace("emoji:", "");
    if (/^[0-9a-f\-]+$/.test(code))
      return <img src={getTwemojiSvgUrl(code)} alt="" className="object-contain shrink-0" style={{ width: size, height: size }} />;
    return <span style={{ fontSize: size, lineHeight: 1 }} className="shrink-0">{code}</span>;
  }
  if (icon.startsWith("lucide:")) {
    const parts = icon.replace("lucide:", "").split(":");
    const Icon = (Lucide as any)[parts[0]];
    if (Icon) return <Icon size={size} color={parts[1] !== "currentColor" ? parts[1] : undefined} className="shrink-0" />;
  }
  if (icon.startsWith("upload:"))
    return <img src={icon.replace("upload:", "")} alt="" className="rounded-sm object-cover shrink-0" style={{ width: size, height: size }} />;
  return <FileText size={size} className="text-gray-400 dark:text-zinc-500 shrink-0" />;
}

// ── 필터 칩 드롭다운 ──────────────────────────────────────────────────────────

function FilterChip({ label, active, onClear, children }: { label: string; active: boolean; onClear: () => void; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(v => !v)} className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
        active
          ? "bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400"
          : "bg-transparent border-gray-300 dark:border-zinc-700 text-gray-600 dark:text-zinc-400 hover:border-gray-400"
      }`}>
        {label}
        {active
          ? <X size={10} onClick={e => { e.stopPropagation(); onClear(); setOpen(false); }} />
          : <ChevronDown size={10} />}
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-20 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg shadow-xl py-1 min-w-40">
          {children}
        </div>
      )}
    </div>
  );
}

// ── 메인 컴포넌트 ─────────────────────────────────────────────────────────────

/**
 * 문맥 안에서 검색어를 굵게 표시한다.
 *
 * dangerouslySetInnerHTML 을 쓰지 않고 문자열을 쪼개 React 노드로 만든다 —
 * 본문은 사용자 입력이라 HTML 로 주입하면 저장형 XSS 가 된다(댓글에서 겪은 그 결함).
 */
function highlightMatch(text: string, term: string) {
  const q = term.trim();
  if (!q) return text;

  const lower = text.toLowerCase();
  const lowerQ = q.toLowerCase();
  const parts: React.ReactNode[] = [];
  let cursor = 0;

  for (let i = lower.indexOf(lowerQ); i !== -1; i = lower.indexOf(lowerQ, cursor)) {
    if (i > cursor) parts.push(text.slice(cursor, i));
    parts.push(
      <mark key={i} className="bg-yellow-200 dark:bg-yellow-500/30 text-inherit rounded px-0.5">
        {text.slice(i, i + q.length)}
      </mark>,
    );
    cursor = i + q.length;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}

export default function SearchModal({ isOpen, onClose, onOpenSettings }: SearchModalProps) {
  const { data: session } = useSession();
  const user: any = session?.user;
  const router = useRouter();
  const { setTheme } = useTheme();
  const { docs: allDocs, openCreateModal } = useSidebarContext();

  const [query, setQuery]               = useState("");
  const [titleOnly, setTitleOnly]       = useState(false);
  const [searchResults, setSearchResults] = useState<Doc[]>([]);
  const [isSearching, setIsSearching]   = useState(false);
  const [selectedIdx, setSelectedIdx]   = useState(0);
  const [creatorFilter, setCreatorFilter] = useState<string | null>(null);
  const [scopeFilter, setScopeFilter]   = useState<{ id: string; title: string } | null>(null);
  const [scopeSearch, setScopeSearch]   = useState("");

  const inputRef    = useRef<HTMLInputElement>(null);
  const listRef     = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 모달 열릴 때 초기화
  useEffect(() => {
    if (isOpen) {
      setQuery(""); setTitleOnly(false); setSearchResults([]); setSelectedIdx(0);
      setCreatorFilter(null); setScopeFilter(null); setScopeSearch("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // ── 커맨드 정의 ──────────────────────────────────────────────────────────────

  const commands = useMemo((): Command[] => {
    const role = user?.role;
    const list: Command[] = [];

    if (role === "USER" || role === "ADMIN") {
      list.push({
        id: "new-team-page",
        icon: <Users size={14} />,
        label: "새 팀 스페이스 페이지",
        description: "팀 스페이스에 새 문서를 만듭니다.",
        keywords: ["새", "팀", "페이지", "문서", "추가", "생성", "만들기", "new", "team"],
        action: () => { openCreateModal("TEAM", null, "팀 스페이스"); onClose(); },
      });
    }

    if (role === "USER") {
      list.push({
        id: "new-personal-page",
        icon: <Plus size={14} />,
        label: "새 개인 페이지",
        description: "개인 스페이스에 새 문서를 만듭니다.",
        keywords: ["새", "개인", "페이지", "문서", "추가", "생성", "만들기", "new", "personal"],
        action: () => { openCreateModal("PERSONAL", null, "개인 페이지"); onClose(); },
      });
    }

    list.push({
      id: "library",
      icon: <Library size={14} />,
      label: "라이브러리 열기",
      description: "내 모든 문서와 최근·즐겨찾기를 한눈에 봅니다.",
      keywords: ["라이브러리", "library", "목록", "전체", "즐겨찾기", "최근"],
      action: () => { router.push("/library"); onClose(); },
    });

    if (onOpenSettings) {
      list.push({
        id: "settings",
        icon: <Settings size={14} />,
        label: "설정 및 관리",
        description: "프로필·테마·멤버 설정을 변경합니다.",
        keywords: ["설정", "관리", "프로필", "테마", "settings", "멤버"],
        action: () => { onOpenSettings(); onClose(); },
      });
    }

    list.push(
      {
        id: "theme-light",
        icon: <Sun size={14} />,
        label: "라이트 모드로 변경",
        description: "화면을 밝은 모드로 전환합니다.",
        keywords: ["라이트", "밝음", "테마", "light", "theme"],
        action: () => { setTheme("light"); onClose(); },
      },
      {
        id: "theme-dark",
        icon: <Moon size={14} />,
        label: "다크 모드로 변경",
        description: "화면을 어두운 모드로 전환합니다.",
        keywords: ["다크", "어둠", "테마", "dark", "theme"],
        action: () => { setTheme("dark"); onClose(); },
      },
      {
        id: "theme-system",
        icon: <Monitor size={14} />,
        label: "시스템 테마 따르기",
        description: "OS 설정에 따라 라이트/다크를 자동으로 적용합니다.",
        keywords: ["시스템", "자동", "테마", "system", "theme"],
        action: () => { setTheme("system"); onClose(); },
      },
      {
        id: "logout",
        icon: <LogOut size={14} />,
        label: "로그아웃",
        description: "현재 계정에서 로그아웃합니다.",
        keywords: ["로그아웃", "logout", "나가기", "종료"],
        action: () => { void revokeServerSession(user?.accessToken).finally(() => signOut({ callbackUrl: "/login" })); onClose(); },
      },
    );

    return list;
  }, [user?.role, user?.accessToken, openCreateModal, router, onOpenSettings, setTheme, onClose]);

  // 쿼리 기반 커맨드 필터
  const matchingCommands = useMemo(() => {
    if (!query.trim()) return commands;
    const q = query.toLowerCase();
    return commands.filter(c =>
      c.label.toLowerCase().includes(q) ||
      c.keywords.some(k => k.toLowerCase().includes(q))
    );
  }, [query, commands]);

  // ── 문서 검색 ────────────────────────────────────────────────────────────────

  const isInSearchMode = query.trim().length > 0;

  const doSearch = useCallback(async (q: string, creator: string | null, scope: string | null, titleOnlyMode: boolean) => {
    if (!q.trim()) { setSearchResults([]); setIsSearching(false); return; }
    setIsSearching(true);
    try {
      const params = new URLSearchParams({ q: q.trim() });
      if (creator) params.set("creator", creator);
      if (scope)   params.set("scope", scope);
      if (titleOnlyMode) params.set("titleOnly", "true");
      const res = await fetch(`${API_URL}/documents/search?${params}`, {
        headers: authOnlyHeaders(user?.accessToken),
      });
      const data = await res.json();
      setSearchResults(Array.isArray(data) ? data : []);
      setSelectedIdx(0);
    } catch { setSearchResults([]); }
    finally { setIsSearching(false); }
  }, [user]);

  useEffect(() => {
    if (!isInSearchMode) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doSearch(query, creatorFilter, scopeFilter?.id ?? null, titleOnly);
    }, 280);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, creatorFilter, scopeFilter, isInSearchMode, titleOnly, doSearch]);

  // ── 표시할 문서 목록 ──────────────────────────────────────────────────────────

  const displayDocs = useMemo((): Doc[] => {
    if (isInSearchMode) return searchResults;
    let docs: Doc[] = allDocs as Doc[];
    if (creatorFilter) docs = docs.filter(d => d.creatorName === creatorFilter);
    if (scopeFilter)   docs = docs.filter(d => d.parentId   === scopeFilter.id);
    return [...docs].sort((a, b) =>
      new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime()
    );
  }, [isInSearchMode, searchResults, allDocs, creatorFilter, scopeFilter]);

  // 검색 중에는 날짜 그룹으로 다시 묶지 않는다.
  //
  // groupDocs 는 문서를 today/week/month/older 로 나눠 그 순서대로 렌더한다. 평소(최근 문서
  // 목록)에는 유용하지만, **검색 결과에 적용하면 서버가 매긴 관련도 순서가 통째로 흩어진다** —
  // 제목이 정확히 일치하는 오래된 문서가 'older' 로 밀려 목록 맨 아래에 처박혔다.
  // 검색 모드에서는 전부 한 덩어리('today' 슬롯)에 담아 서버가 준 순서를 그대로 보여 준다.
  const grouped = useMemo(
    () => (isInSearchMode
      ? { today: displayDocs, week: [], month: [], older: [] } as Record<Group, Doc[]>
      : groupDocs(displayDocs)),
    [displayDocs, isInSearchMode],
  );

  // ── 통합 플랫 리스트 (커맨드 + 문서) ─────────────────────────────────────────

  const flatList = useMemo((): FlatItem[] => {
    const cmdItems: FlatItem[] = matchingCommands.map(c => ({ type: "command", command: c }));
    const ORDER: Group[] = ["today", "week", "month", "older"];
    const docItems: FlatItem[] = ORDER.flatMap(g => (grouped[g] || []).map(d => ({ type: "doc", doc: d })));
    return [...cmdItems, ...docItems];
  }, [matchingCommands, grouped]);

  const selectedItem = flatList[selectedIdx] ?? null;

  // 생성자 목록
  const creators = useMemo(
    () => [...new Set((allDocs as Doc[]).map(d => d.creatorName).filter(Boolean))] as string[],
    [allDocs]
  );

  // 범위(scope) 후보
  const scopeCandidates = useMemo(() => {
    if (!scopeSearch.trim()) return (allDocs as Doc[]).slice(0, 20);
    return (allDocs as Doc[]).filter(d => d.title?.toLowerCase().includes(scopeSearch.toLowerCase())).slice(0, 20);
  }, [allDocs, scopeSearch]);

  const scopeDocMap = useMemo(() => {
    const map = new Map<string, Doc>();
    (allDocs as Doc[]).forEach(d => map.set(d.id, d));
    return map;
  }, [allDocs]);

  const buildScopeBreadcrumb = (doc: Doc): string => {
    const parts: string[] = [];
    let cur = doc.parentId ? scopeDocMap.get(doc.parentId) : undefined;
    let depth = 0;
    while (cur && depth < 6) {
      parts.unshift(cur.title || "새 페이지");
      cur = cur.parentId ? scopeDocMap.get(cur.parentId) : undefined;
      depth++;
    }
    if (parts.length === 0) {
      return doc.workspaceType === "PERSONAL" ? "개인 스페이스" : "팀 스페이스";
    }
    return parts.join(" / ");
  };

  // ── 키보드 네비게이션 ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, flatList.length - 1)); }
      if (e.key === "ArrowUp")   { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
      if (e.key === "Enter" && selectedItem) {
        if (selectedItem.type === "command") {
          selectedItem.command.action();
        } else {
          const doc = selectedItem.doc;
          if (e.ctrlKey) { window.open(`/doc/${doc.id}`, "_blank"); onClose(); }
          else { router.push(`/doc/${doc.id}`); onClose(); }
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, flatList, selectedItem, router, onClose]);

  // 선택 항목 스크롤
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${selectedIdx}"]`)?.scrollIntoView({ block: "nearest" });
  }, [selectedIdx]);

  if (!isOpen) return null;

  const navigate = (id: string, newTab = false) => {
    if (newTab) window.open(`/doc/${id}`, "_blank");
    else router.push(`/doc/${id}`);
    onClose();
  };

  // 문서 섹션 렌더링 시 인덱스 추적용 (커맨드 수만큼 offset)
  const cmdCount = matchingCommands.length;
  const ORDER_GROUPS: Group[] = ["today", "week", "month", "older"];

  // 문서 전체 개수 (그룹별 합산)
  const totalDocCount = ORDER_GROUPS.reduce((s, g) => s + (grouped[g]?.length || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white dark:bg-zinc-900 w-185 max-w-[95vw] rounded-xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 dark:border-zinc-800"
        style={{ height: 600 }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── 검색 입력 ── */}
        <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 dark:border-zinc-800 shrink-0">
          <Search size={16} className="text-gray-400 dark:text-zinc-500 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setSelectedIdx(0); }}
            placeholder="검색 또는 명령어 입력…"
            className="flex-1 outline-none text-sm bg-transparent text-gray-800 dark:text-zinc-100 placeholder-gray-400 dark:placeholder-zinc-500"
          />
          {query && (
            <button onClick={() => { setQuery(""); setSearchResults([]); setSelectedIdx(0); inputRef.current?.focus(); }}
              className="p-1 rounded text-gray-400 dark:text-zinc-500 hover:bg-gray-100 dark:hover:bg-zinc-800">
              <X size={14} />
            </button>
          )}
          <button onClick={onClose} className="p-1 rounded text-gray-400 dark:text-zinc-500 hover:bg-gray-100 dark:hover:bg-zinc-800">
            <X size={16} />
          </button>
        </div>

        {/* ── 필터 칩 행 (문서 결과가 있을 때만) ── */}
        {totalDocCount > 0 && (
          <div className="flex items-center gap-2 px-4 py-1.5 border-b border-gray-100 dark:border-zinc-800 shrink-0">
            <button
              onClick={() => { setTitleOnly(v => !v); if (!titleOnly && query.trim()) setSelectedIdx(0); }}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                titleOnly
                  ? "bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400"
                  : "bg-transparent border-gray-300 dark:border-zinc-700 text-gray-600 dark:text-zinc-400 hover:border-gray-400"
              }`}
            >
              <span className="font-mono text-[10px] font-bold leading-none">Aa</span>
              제목만
            </button>

            <FilterChip label={creatorFilter ? `생성자: ${creatorFilter}` : "생성자"} active={!!creatorFilter} onClear={() => setCreatorFilter(null)}>
              {creators.length === 0
                ? <p className="px-3 py-2 text-xs text-gray-400 dark:text-zinc-500">편집자 없음</p>
                : creators.map(c => (
                  <button key={c} onClick={() => setCreatorFilter(creatorFilter === c ? null : c)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 dark:text-zinc-200 hover:bg-gray-50 dark:hover:bg-zinc-700">
                    {creatorFilter === c && <Check size={10} className="text-blue-500 shrink-0" />}
                    <span>{c}</span>
                  </button>
                ))
              }
            </FilterChip>

            <FilterChip label={scopeFilter ? `범위: ${scopeFilter.title}` : "검색 범위:"} active={!!scopeFilter} onClear={() => { setScopeFilter(null); setScopeSearch(""); }}>
              <div className="p-2 border-b border-gray-100 dark:border-zinc-700">
                <input autoFocus type="text" value={scopeSearch} onChange={e => setScopeSearch(e.target.value)}
                  placeholder="페이지 검색하기"
                  className="w-full text-xs px-2 py-1.5 bg-gray-50 dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded outline-none placeholder-gray-400 dark:placeholder-zinc-500 text-gray-800 dark:text-zinc-100" />
              </div>
              <div className="max-h-44 overflow-y-auto py-1">
                {scopeCandidates.map(d => (
                  <button key={d.id}
                    onClick={() => { setScopeFilter({ id: d.id, title: d.title || "새 페이지" }); setScopeSearch(""); }}
                    className="w-full flex items-center gap-1.5 px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-zinc-700">
                    <DocIcon icon={d.icon} size={12} />
                    <span className="flex-1 min-w-0 flex flex-col items-start">
                      <span className="text-gray-800 dark:text-zinc-100 truncate w-full">{d.title || "새 페이지"}</span>
                      <span className="text-gray-400 dark:text-zinc-500 truncate w-full">{buildScopeBreadcrumb(d)}</span>
                    </span>
                  </button>
                ))}
              </div>
            </FilterChip>
          </div>
        )}

        {/* ── 본문 — 좌(목록) + 우(미리보기/설명) ── */}
        <div className="flex flex-1 min-h-0">
          {/* 결과 목록 */}
          <div ref={listRef} className="w-[55%] border-r border-gray-100 dark:border-zinc-800 overflow-y-auto">
            {isSearching ? (
              <div className="flex items-center justify-center h-full text-sm text-gray-400 dark:text-zinc-500">검색 중...</div>
            ) : (
              <div className="py-1">

                {/* 커맨드 섹션 */}
                {matchingCommands.length > 0 && (
                  <div>
                    <p className="px-4 pt-1.5 pb-0.5 text-[10px] font-semibold text-gray-400 dark:text-zinc-500 uppercase tracking-wide flex items-center gap-1">
                      <Zap size={9} />빠른 실행
                    </p>
                    {matchingCommands.map((cmd, i) => {
                      const idx = i;
                      const isSel = idx === selectedIdx;
                      return (
                        <button
                          key={cmd.id}
                          data-idx={idx}
                          onClick={cmd.action}
                          onMouseEnter={() => setSelectedIdx(idx)}
                          className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors ${
                            isSel ? "bg-blue-50 dark:bg-blue-900/20" : "hover:bg-gray-50 dark:hover:bg-zinc-800/60"
                          }`}
                        >
                          <div className={`shrink-0 w-5 h-5 rounded flex items-center justify-center ${
                            isSel
                              ? "bg-blue-100 dark:bg-blue-800/50 text-blue-600 dark:text-blue-300"
                              : "bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-zinc-400"
                          }`}>
                            {cmd.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className={`text-[13px] font-medium truncate block ${isSel ? "text-blue-700 dark:text-blue-300" : "text-gray-800 dark:text-zinc-100"}`}>
                              {cmd.label}
                            </span>
                          </div>
                          {isSel && (
                            <span className="text-[10px] text-gray-400 dark:text-zinc-500 shrink-0 bg-gray-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded font-mono">
                              Enter
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* 문서 섹션 */}
                {totalDocCount > 0 && (
                  <>
                    {matchingCommands.length > 0 && <div className="mt-1 mb-0.5 border-t border-gray-100 dark:border-zinc-800" />}
                    {(["today", "week", "month", "older"] as Group[]).map(g => {
                      const items = grouped[g];
                      if (!items?.length) return null;
                      return (
                        <div key={g}>
                          <p className="px-4 pt-1.5 pb-0.5 text-[10px] font-semibold text-gray-400 dark:text-zinc-500 uppercase tracking-wide">
                            {isInSearchMode ? "문서" : GROUP_LABELS[g]}
                          </p>
                          {items.map((doc) => {
                            const docFlatIdx = cmdCount + ORDER_GROUPS.slice(0, ORDER_GROUPS.indexOf(g)).reduce((s, pg) => s + (grouped[pg]?.length || 0), 0) + items.indexOf(doc);
                            const isSel = docFlatIdx === selectedIdx;
                            return (
                              <button
                                key={doc.id}
                                data-idx={docFlatIdx}
                                onClick={() => navigate(doc.id)}
                                onMouseEnter={() => setSelectedIdx(docFlatIdx)}
                                className={`w-full flex items-start gap-2 px-3 py-1.5 text-left transition-colors ${
                                  isSel ? "bg-gray-100 dark:bg-zinc-800" : "hover:bg-gray-50 dark:hover:bg-zinc-800/60"
                                }`}
                              >
                                <div className="shrink-0 w-4 flex items-center justify-center mt-0.5">
                                  <DocIcon icon={doc.icon} size={13} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  {/* 제목에서도 검색어를 강조한다 */}
                                  <span className="text-[13px] text-gray-800 dark:text-zinc-100 font-medium truncate block">
                                    {highlightMatch(doc.title || "새 페이지", query)}
                                  </span>
                                  {doc.isSuggestion && (
                                    <span className="mt-0.5 block text-[10px] font-semibold text-blue-500 dark:text-blue-400">혹시 이걸 찾으셨나요?</span>
                                  )}
                                  {/* 목록에서 바로 "왜 걸렸는지"를 보여 준다.
                                      예전에는 우측 미리보기에만 있어서, 목록만 훑는 동안에는
                                      제목만 보이고 매칭 근거가 전혀 드러나지 않았다. */}
                                  {doc.matchContext ? (
                                    // truncate(한 줄)로 자르면 정작 강조해야 할 검색어가 잘려 나간다
                                    // (실제로 노란 강조가 행 오른쪽 끝에 걸려 안 보였다). 두 줄까지 허용한다.
                                    <span className="text-[11px] text-gray-500 dark:text-zinc-400 line-clamp-2 block leading-snug">
                                      {doc.matchedIn === "comment" && (
                                        <span className="text-blue-500 dark:text-blue-400 mr-1 font-semibold">댓글</span>
                                      )}
                                      {highlightMatch(doc.matchContext, query)}
                                    </span>
                                  ) : doc.breadcrumb ? (
                                    <span className="text-[11px] text-gray-400 dark:text-zinc-500 truncate block">— {doc.breadcrumb}</span>
                                  ) : null}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                  </>
                )}

                {/* 빈 상태 */}
                {matchingCommands.length === 0 && totalDocCount === 0 && isInSearchMode && (
                  <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-400 dark:text-zinc-500 py-16">
                    <FileText size={24} strokeWidth={1.5} />
                    <p className="text-sm">검색 결과 없음</p>
                  </div>
                )}

              </div>
            )}
          </div>

          {/* 미리보기 / 커맨드 설명 패널 */}
          <div className="flex-1 flex flex-col overflow-hidden bg-gray-50 dark:bg-zinc-900/50">
            {selectedItem?.type === "command" ? (
              /* 커맨드 설명 */
              <div className="flex flex-col items-center justify-center h-full gap-4 p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                  {(() => {
                    const child = selectedItem.command.icon as any;
                    return child?.props
                      ? { ...child, props: { ...child.props, size: 22 } }
                      : child;
                  })()}
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800 dark:text-zinc-100">{selectedItem.command.label}</p>
                  <p className="text-xs text-gray-500 dark:text-zinc-400 mt-1 leading-relaxed">{selectedItem.command.description}</p>
                </div>
                <span className="text-[11px] text-gray-400 dark:text-zinc-500 bg-gray-200 dark:bg-zinc-800 px-2 py-1 rounded-md font-mono">
                  Enter 로 실행
                </span>
              </div>
            ) : selectedItem?.type === "doc" ? (
              <>
                {/* 문서 액션 버튼 */}
                <div className="flex justify-end gap-1 p-2 shrink-0">
                  <button onClick={() => navigate(selectedItem.doc.id)} title="열기"
                    className="p-1.5 rounded text-gray-400 dark:text-zinc-500 hover:bg-gray-200 dark:hover:bg-zinc-800 transition-colors">
                    <Link2 size={13} />
                  </button>
                  <button onClick={() => navigate(selectedItem.doc.id, true)} title="새 탭에서 열기"
                    className="p-1.5 rounded text-gray-400 dark:text-zinc-500 hover:bg-gray-200 dark:hover:bg-zinc-800 transition-colors">
                    <ExternalLink size={13} />
                  </button>
                </div>
                {/* 문서 텍스트 미리보기 */}
                <div className="flex-1 overflow-y-auto px-5 pb-5">
                  <p className="text-base font-bold text-gray-800 dark:text-zinc-100 mb-3 leading-snug">{selectedItem.doc.title || "새 페이지"}</p>
                  {/* 검색으로 들어온 결과면 "왜 걸렸는지"(매칭 문맥)를 먼저 보여 준다.
                      본문 앞부분만 보여 주면 검색어가 3천 자 뒤에 있을 때 흔적조차 안 보인다. */}
                  {selectedItem.doc.matchContext ? (
                    <>
                      <p className="text-[10px] font-semibold text-blue-500 dark:text-blue-400 mb-1.5">
                        {selectedItem.doc.matchedIn === "comment" ? "댓글에서 일치" : "본문에서 일치"}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-zinc-400 leading-relaxed whitespace-pre-wrap mb-3">
                        {highlightMatch(selectedItem.doc.matchContext, query)}
                      </p>
                    </>
                  ) : null}
                  {selectedItem.doc.textSnippet ? (
                    <p className="text-sm text-gray-600 dark:text-zinc-400 leading-relaxed whitespace-pre-wrap">
                      {selectedItem.doc.textSnippet}
                    </p>
                  ) : !selectedItem.doc.matchContext ? (
                    <p className="text-xs text-gray-400 dark:text-zinc-600 italic">내용 없음</p>
                  ) : null}
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center gap-2 text-gray-300 dark:text-zinc-700">
                <Search size={28} strokeWidth={1} />
                <p className="text-xs">검색하거나 명령어를 실행하세요</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
